import { Rng } from './rng';
import { BASE_SUIT, type SuitStats } from './suit';
import { TRANSIT as T } from './tuning';

/**
 * Light-speed transit (GDD: Universe and level design): a side-scrolling shoot-'em-up between
 * systems. The ship (the suit at light speed) fires on its own; the player only steers through
 * asteroids, raider patrols and Starlight crystals. Hits cost armor, not the run, unless the last
 * plate goes. Pure TypeScript and seeded: the same seed flies the same waves.
 */

export interface TransitIntents {
  /** Where a drag wants the ship (field pixels), or null when not dragging. */
  readonly target: { readonly x: number; readonly y: number } | null;
  /** Keyboard steering, -1 to 1 on each axis. */
  readonly moveX: number;
  readonly moveY: number;
}

export const NO_TRANSIT_INPUT: TransitIntents = { target: null, moveX: 0, moveY: 0 };

export type RockSize = 'big' | 'small';

export interface Ship {
  /** Centre, in field pixels. */
  x: number;
  y: number;
  armor: number;
  starlight: number;
  invuln: number;
  fireCooldown: number;
}

export interface Rock {
  readonly id: number;
  readonly size: RockSize;
  readonly r: number;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  hp: number;
  hitFlash: number;
  alive: boolean;
}

export interface Raider {
  readonly id: number;
  x: number;
  y: number;
  readonly baseY: number;
  readonly amp: number;
  readonly phase: number;
  age: number;
  hp: number;
  hitFlash: number;
  alive: boolean;
}

export interface Crystal {
  readonly id: number;
  /** Starlight, or a rare material shard (kept through rewinds). */
  readonly kind: 'starlight' | 'shard';
  x: number;
  y: number;
  taken: boolean;
}

export interface TransitShot {
  readonly id: number;
  readonly hostile: boolean;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  alive: boolean;
}

export type TransitEvent =
  | { type: 'fire' }
  | { type: 'rockHit'; id: number }
  | { type: 'rockBroken'; id: number; size: RockSize; x: number; y: number }
  | { type: 'raiderHit'; id: number }
  | { type: 'raiderKilled'; id: number; x: number; y: number }
  | { type: 'boltFire' }
  | { type: 'pickup'; amount: number }
  | { type: 'shard' }
  | { type: 'hurt' }
  | { type: 'rewind' }
  | { type: 'arrived' };

export type TransitSpawn =
  | { at: number; kind: 'rock'; size: RockSize; y: number; vx: number; vy: number }
  | { at: number; kind: 'raider'; y: number; amp: number; phase: number }
  | { at: number; kind: 'crystal'; y: number };

/** The flight in stretches (seconds): a calm start, rock fields, raider patrols, a clear end. */
const STRETCHES = [
  { from: 0, to: 8, rockEvery: 70, bigChance: 0.1, raiderEvery: 0, crystalEvery: 0 },
  { from: 8, to: 22, rockEvery: 28, bigChance: 0.35, raiderEvery: 0, crystalEvery: 150 },
  { from: 22, to: 34, rockEvery: 90, bigChance: 0.3, raiderEvery: 240, crystalEvery: 200 },
  { from: 34, to: 46, rockEvery: 20, bigChance: 0.45, raiderEvery: 0, crystalEvery: 120 },
  { from: 46, to: 56, rockEvery: 45, bigChance: 0.4, raiderEvery: 240, crystalEvery: 180 },
] as const;

const RAIDERS_PER_WAVE = 4;
const RAIDER_SPACING_TICKS = 20;
const MARGIN = 12;

function between(rng: Rng, lo: number, hi: number): number {
  return lo + rng.nextFloat() * (hi - lo);
}

/** Everything that will fly in during a transit, in order, from the seed. */
export function planTransit(seed: string): TransitSpawn[] {
  const rng = Rng.fromSeed(`${seed}/transit-plan`);
  const out: TransitSpawn[] = [];
  const y = () => between(rng, MARGIN * 2, T.height - MARGIN * 2);
  for (const s of STRETCHES) {
    const from = s.from * 60;
    const to = s.to * 60;
    for (let at = from; at < to; at += s.rockEvery + rng.nextInt(Math.ceil(s.rockEvery / 3))) {
      const big = rng.nextFloat() < s.bigChance;
      out.push({
        at,
        kind: 'rock',
        size: big ? 'big' : 'small',
        y: y(),
        vx: -between(rng, big ? 1.1 : 1.5, big ? 1.8 : 2.6),
        vy: between(rng, -0.3, 0.3),
      });
    }
    if (s.raiderEvery > 0) {
      for (let at = from + 30; at < to; at += s.raiderEvery) {
        const baseY = between(rng, 60, T.height - 60);
        const amp = between(rng, 20, 45);
        for (let i = 0; i < RAIDERS_PER_WAVE; i++) {
          out.push({ at: at + i * RAIDER_SPACING_TICKS, kind: 'raider', y: baseY, amp, phase: 0 });
        }
      }
    }
    if (s.crystalEvery > 0) {
      for (let at = from + 40; at < to; at += s.crystalEvery)
        out.push({ at, kind: 'crystal', y: y() });
    }
  }
  return out.sort((a, b) => a.at - b.at);
}

export class TransitSim {
  tick = 0;
  ship: Ship;
  rocks: Rock[] = [];
  raiders: Raider[] = [];
  crystals: Crystal[] = [];
  shots: TransitShot[] = [];
  arrived = false;
  /** Times the suit has rewound in this session (carried in from the run). */
  rewinds = 0;
  private readonly plan: TransitSpawn[];
  private nextSpawn = 0;
  private nextId = 1;
  private events: TransitEvent[] = [];
  private readonly rng: Rng;

  constructor(
    /**
     * Field width in pixels: the screen's width, so things enter at its right edge. The scene
     * updates it when the screen turns.
     */
    public width: number,
    seed: string,
    /** What the suit carries in (absent: full). */
    carry?: { starlight: number; armor: number },
    /** The suit as the lab has built it. */
    readonly suit: SuitStats = BASE_SUIT,
  ) {
    this.plan = planTransit(seed);
    this.rng = Rng.fromSeed(`${seed}/transit-play`);
    this.ship = {
      x: Math.round(width * 0.2),
      y: T.height / 2,
      armor: carry?.armor ?? suit.armorMax,
      starlight: carry?.starlight ?? suit.starlightMax,
      invuln: 0,
      fireCooldown: T.fireEvery,
    };
  }

  /** How far along the flight is, 0 to 1. */
  get progress(): number {
    return Math.min(1, this.tick / T.durationTicks);
  }

  step(input: TransitIntents): readonly TransitEvent[] {
    this.events = [];
    this.tick++;
    this.spawn();
    this.steer(input);
    this.fire();
    this.move();
    this.hitTargets();
    this.hitShip();
    this.cleanUp();
    if (!this.arrived && this.tick >= T.durationTicks) {
      this.arrived = true;
      this.emit({ type: 'arrived' });
    }
    return this.events;
  }

  private emit(e: TransitEvent): void {
    this.events.push(e);
  }

  private spawn(): void {
    const enterX = this.width + 20;
    for (
      let s = this.plan[this.nextSpawn];
      s && s.at <= this.tick;
      s = this.plan[++this.nextSpawn]
    ) {
      if (s.kind === 'rock') this.addRock(s.size, enterX, s.y, s.vx, s.vy);
      else if (s.kind === 'raider') {
        this.raiders.push({
          id: this.nextId++,
          x: enterX,
          y: s.y,
          baseY: s.y,
          amp: s.amp,
          phase: s.phase,
          age: 0,
          hp: T.raiderHp,
          hitFlash: 0,
          alive: true,
        });
      } else {
        this.crystals.push({
          id: this.nextId++,
          kind: 'starlight',
          x: enterX,
          y: s.y,
          taken: false,
        });
      }
    }
  }

  private addRock(size: RockSize, x: number, y: number, vx: number, vy: number): void {
    this.rocks.push({
      id: this.nextId++,
      size,
      r: size === 'big' ? T.bigRockRadius : T.smallRockRadius,
      x,
      y,
      vx,
      vy,
      hp: size === 'big' ? T.bigRockHp : 1,
      hitFlash: 0,
      alive: true,
    });
  }

  private steer(input: TransitIntents): void {
    const s = this.ship;
    if (s.invuln > 0) s.invuln--;
    if (input.target) {
      const dx = input.target.x - s.x;
      const dy = input.target.y - s.y;
      const dist = Math.hypot(dx, dy);
      const step = Math.min(dist, T.shipSpeed);
      if (dist > 0) {
        s.x += (dx / dist) * step;
        s.y += (dy / dist) * step;
      }
    } else {
      s.x += Math.max(-1, Math.min(1, input.moveX)) * T.shipKeySpeed;
      s.y += Math.max(-1, Math.min(1, input.moveY)) * T.shipKeySpeed;
    }
    s.x = Math.max(MARGIN, Math.min(this.width - MARGIN, s.x));
    s.y = Math.max(MARGIN, Math.min(T.height - MARGIN, s.y));
  }

  private fire(): void {
    const s = this.ship;
    if (--s.fireCooldown > 0) return;
    s.fireCooldown = T.fireEvery;
    this.shots.push({
      id: this.nextId++,
      hostile: false,
      x: s.x + 8,
      y: s.y,
      vx: T.laserSpeed,
      vy: 0,
      alive: true,
    });
    this.emit({ type: 'fire' });
  }

  private move(): void {
    for (const r of this.rocks) {
      r.x += r.vx;
      r.y += r.vy;
      if (r.hitFlash > 0) r.hitFlash--;
    }
    const s = this.ship;
    for (const r of this.raiders) {
      r.age++;
      r.x -= T.raiderSpeed;
      r.y = r.baseY + Math.sin(r.age * 0.05 + r.phase) * r.amp;
      if (r.hitFlash > 0) r.hitFlash--;
      // Shoots at the ship now and then, while on screen and ahead of it.
      if (r.age % T.raiderFireEvery === 60 && r.x < this.width - 30 && r.x > s.x + 90) {
        const dx = s.x - r.x;
        const dy = s.y - r.y;
        const len = Math.hypot(dx, dy) || 1;
        this.shots.push({
          id: this.nextId++,
          hostile: true,
          x: r.x - 6,
          y: r.y,
          vx: (dx / len) * T.boltSpeed,
          vy: (dy / len) * T.boltSpeed,
          alive: true,
        });
        this.emit({ type: 'boltFire' });
      }
    }
    for (const c of this.crystals) c.x -= 1.2;
    for (const shot of this.shots) {
      shot.x += shot.vx;
      shot.y += shot.vy;
    }
  }

  private hitTargets(): void {
    for (const shot of this.shots) {
      // Only what's on screen can be hit: no breaking rocks before they are seen.
      if (shot.hostile || !shot.alive || shot.x > this.width) continue;
      const rock = this.rocks.find(
        (r) => r.alive && Math.hypot(r.x - shot.x, r.y - shot.y) < r.r + 2,
      );
      if (rock) {
        shot.alive = false;
        this.damageRock(rock);
        continue;
      }
      const raider = this.raiders.find(
        (r) => r.alive && Math.abs(r.x - shot.x) < 9 && Math.abs(r.y - shot.y) < 7,
      );
      if (raider) {
        shot.alive = false;
        raider.hp--;
        raider.hitFlash = 6;
        this.emit({ type: 'raiderHit', id: raider.id });
        if (raider.hp <= 0) this.killRaider(raider);
      }
    }
  }

  private damageRock(rock: Rock): void {
    rock.hp--;
    rock.hitFlash = 5;
    this.emit({ type: 'rockHit', id: rock.id });
    if (rock.hp > 0) return;
    rock.alive = false;
    this.emit({ type: 'rockBroken', id: rock.id, size: rock.size, x: rock.x, y: rock.y });
    // A big rock splits into two small ones flying apart, and sometimes holds a shard.
    if (rock.size === 'big') {
      if (this.rng.chance(1, 4)) {
        this.crystals.push({
          id: this.nextId++,
          kind: 'shard',
          x: rock.x,
          y: rock.y,
          taken: false,
        });
      }
      this.addRock('small', rock.x, rock.y - 6, rock.vx * 1.2, rock.vy - 0.6);
      this.addRock('small', rock.x, rock.y + 6, rock.vx * 1.2, rock.vy + 0.6);
    }
  }

  private killRaider(r: Raider): void {
    r.alive = false;
    this.emit({ type: 'raiderKilled', id: r.id, x: r.x, y: r.y });
    if (this.rng.chance(1, 3)) {
      this.crystals.push({ id: this.nextId++, kind: 'starlight', x: r.x, y: r.y, taken: false });
    }
  }

  private hitShip(): void {
    const s = this.ship;
    for (const c of this.crystals) {
      if (!c.taken && Math.hypot(c.x - s.x, c.y - s.y) < 14) {
        c.taken = true;
        if (c.kind === 'shard') {
          this.emit({ type: 'shard' });
        } else {
          s.starlight = Math.min(this.suit.starlightMax, s.starlight + T.crystalStarlight);
          this.emit({ type: 'pickup', amount: T.crystalStarlight });
        }
      }
    }
    if (s.invuln > 0) return;
    for (const r of this.rocks) {
      if (r.alive && Math.hypot(r.x - s.x, r.y - s.y) < r.r + T.shipRadius) {
        if (r.size === 'small') r.alive = false;
        this.hurt();
        return;
      }
    }
    for (const r of this.raiders) {
      if (r.alive && Math.abs(r.x - s.x) < 10 && Math.abs(r.y - s.y) < 8) {
        this.killRaider(r);
        this.hurt();
        return;
      }
    }
    for (const shot of this.shots) {
      if (shot.hostile && shot.alive && Math.hypot(shot.x - s.x, shot.y - s.y) < T.shipRadius + 3) {
        shot.alive = false;
        this.hurt();
        return;
      }
    }
  }

  private hurt(): void {
    const s = this.ship;
    s.armor--;
    s.invuln = T.invulnTicks;
    this.emit({ type: 'hurt' });
    if (s.armor <= 0) {
      this.rewinds++;
      this.emit({ type: 'rewind' });
    }
  }

  private cleanUp(): void {
    const inField = (x: number, y: number, pad: number) =>
      x > -pad && x < this.width + pad + 20 && y > -pad && y < T.height + pad;
    this.rocks = this.rocks.filter((r) => r.alive && inField(r.x, r.y, 40));
    this.raiders = this.raiders.filter((r) => r.alive && inField(r.x, r.y, 40));
    this.crystals = this.crystals.filter((c) => !c.taken && inField(c.x, c.y, 20));
    this.shots = this.shots.filter((s) => s.alive && inField(s.x, s.y, 10));
  }
}
